import { useStore } from 'zustand';

import { getCurrentUserDetails } from '@dynatrace-sdk/app-environment';
import { stateClient } from '@dynatrace-sdk/client-state';

import { type AppStateContextValue } from './AppStateContext.js';

const currentUser = getCurrentUserDetails();

/**
 * @internal
 */
export interface _SetOptions {
  validUntilTime?: string | null;
}

/**
 * @internal
 */
export type _UseAppStateResult<T> = {
  value: T | undefined;
  isLoading: boolean;
  error: Error | null;
  setValue: (
    value: T | ((prev: T | undefined) => T),
    options?: _SetOptions,
  ) => Promise<void>;
  remove: () => Promise<void>;
};

type MutateFns = {
  set: (
    key: string,
    value: string,
    validUntilTime: string | null | undefined,
  ) => Promise<void>;
  remove: (key: string) => Promise<void>;
};

export const userScopedMutateFns: MutateFns = {
  set(key: string, value: string, validUntilTime: string | null | undefined) {
    return stateClient.setUserAppState({
      key,
      body: {
        value,
        validUntilTime: validUntilTime ?? null,
        modificationInfo: {
          lastModifiedBy: currentUser.id,
          lastModifiedTime: new Date(),
        },
      },
    });
  },
  remove(key: string) {
    return stateClient.deleteUserAppState({ key });
  },
};

export const appScopedMutateFns: MutateFns = {
  set(key: string, value: string, validUntilTime: string | null | undefined) {
    return stateClient.setAppState({
      key,
      body: {
        value,
        validUntilTime: validUntilTime ?? null,
        modificationInfo: {
          lastModifiedBy: currentUser.id,
          lastModifiedTime: new Date(),
        },
      },
    });
  },
  remove(key: string) {
    return stateClient.deleteAppState({ key });
  },
};

/**
 * @internal
 */
export function useAppStateInternal<T = unknown>(
  key: string,
  mutateFns: MutateFns,
  { store: activeStore, scope, queryClient }: AppStateContextValue,
): _UseAppStateResult<T> {
  const rawValue = useStore(activeStore, (s) => s.data[key]);
  const isLoading = useStore(activeStore, (s) => s.isLoading);
  const error = useStore(activeStore, (s) => s.error);

  const value =
    rawValue !== undefined ? (JSON.parse(rawValue) as T) : undefined;

  async function set(
    newValue: T | ((prev: T) => T),
    options?: _SetOptions,
  ): Promise<void> {
    // The query cache is the source of truth — it is populated synchronously
    // when the bulk fetch promise resolves, while the store is only synced via
    // a re-render cycle that may lag behind under batched updates.
    const cached = queryClient
      .getQueryData<Array<{ key: string; value?: string }>>([
        'app-state',
        scope,
      ])
      ?.find((item) => item.key === key)?.value;
    const previous = cached ?? activeStore.getState().data[key];
    let updatedValue: T;
    if (typeof newValue === 'function') {
      const currentValue =
        previous !== undefined ? (JSON.parse(previous) as T) : undefined;
      updatedValue = (newValue as (prev: T | undefined) => T)(currentValue);
    } else {
      updatedValue = newValue as T;
    }

    try {
      const serialized = JSON.stringify(updatedValue);

      // Optimistic update
      activeStore.setState((s) => ({ data: { ...s.data, [key]: serialized } }));

      await mutateFns.set(key, serialized, options?.validUntilTime);
      // Update query cache so no follow-up GET is needed
      queryClient.setQueryData(
        ['app-state', scope],
        (old: Array<{ key: string; value?: string }> | undefined) => {
          if (!old) {
            return old;
          }
          const idx = old.findIndex((item) => item.key === key);
          if (idx === -1) {
            return [...old, { key, value: serialized }];
          }
          return old.map((item, i) =>
            i === idx ? { ...item, value: serialized } : item,
          );
        },
      );
    } catch (err) {
      // Rollback
      activeStore.setState((s) => {
        const next = { ...s.data };
        if (previous === undefined) {
          delete next[key];
        } else {
          next[key] = previous;
        }
        return { data: next };
      });
      // Read onError imperatively from the store — avoids React context churn.
      activeStore.getState().onError?.(err as Error);
    }
  }

  async function remove(): Promise<void> {
    const previous = activeStore.getState().data[key];

    // Optimistic update
    activeStore.setState((s) => {
      const next = { ...s.data };
      delete next[key];
      return { data: next };
    });

    try {
      await mutateFns.remove(key);
      // Update query cache
      queryClient.setQueryData(
        ['app-state', scope],
        (old: Array<{ key: string; value?: string }> | undefined) => {
          if (!old) {
            return old;
          }
          return old.filter((item) => item.key !== key);
        },
      );
    } catch (err) {
      // Rollback
      if (previous !== undefined) {
        activeStore.setState((s) => ({
          data: { ...s.data, [key]: previous },
        }));
      }
      activeStore.getState().onError?.(err as Error);
    }
  }

  return { value, isLoading, error, setValue: set, remove };
}
